import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, PauseIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

// Types
interface RawCurriculumItem {
  year: number;
  math_study_pillar: string;
  module_id: string;
  module_generic_name: string;
  requires_1: string;
  requires_2: string;
  submodule: string;
  submodule_order: number;
}

interface ProcessedModule {
  module_id: string;
  module_name: string;
  pillar: string;
  year: number;
  requires_1: string;
  requires_2: string;
  submodules: { name: string; order: number }[];
}

interface LogEntry {
  time: number;
  type: 'correct' | 'incorrect' | 'decay' | 'system';
  moduleId: string;
  submodule: string;
  message: string;
  masteryChange?: { from: MasteryLevel; to: MasteryLevel };
}

type MasteryLevel = 'none' | 'bronze' | 'silver' | 'gold';

// Color mapping
const PILLAR_COLORS: Record<string, string> = {
  numbers_and_algebra: '#10b981',
  measurement_space: '#0ea5e9',
  statistics: '#f43f5e',
  calculus: '#8b5cf6',
  financial_mathematics: '#f59e0b',
};

const PILLAR_NAMES: Record<string, string> = {
  numbers_and_algebra: 'Numbers & Algebra',
  measurement_space: 'Measurement & Space',
  statistics: 'Statistics',
  calculus: 'Calculus',
  financial_mathematics: 'Financial Maths',
};

const PILLAR_ORDER = ['numbers_and_algebra', 'measurement_space', 'statistics', 'calculus', 'financial_mathematics'];

const MASTERY_COLORS: Record<MasteryLevel, string> = {
  none: '#334155',
  bronze: '#cd7f32',
  silver: '#c0c0c0',
  gold: '#ffd700',
};

const MASTERY_PROGRESS: Record<MasteryLevel, number> = {
  none: 0,
  bronze: 0.33,
  silver: 0.66,
  gold: 1,
};

const InvSlide04bMasteryDemo: React.FC = () => {
  const [rawData, setRawData] = useState<RawCurriculumItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [simTime, setSimTime] = useState(0);
  const [mastery, setMastery] = useState<Record<string, MasteryLevel>>({});
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [activeSubmodule, setActiveSubmodule] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<{ key: string; x: number; y: number; moduleName: string; subName: string } | null>(null);
  const [zoom, setZoom] = useState(0.7);
  const logRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load curriculum data
  useEffect(() => {
    fetch('/curriculum-data.json')
      .then(res => res.json())
      .then((data: RawCurriculumItem[]) => {
        setRawData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load curriculum data:', err);
        setLoading(false);
      });
  }, []);

  // Process data into modules with submodules
  const { modulesByYear, allSubmoduleKeys, prerequisiteLinks } = useMemo(() => {
    if (rawData.length === 0) return { modulesByYear: new Map(), allSubmoduleKeys: [] as string[], prerequisiteLinks: [] as { from: string; to: string }[] };

    const moduleMap = new Map<string, ProcessedModule>();
    const links: { from: string; to: string }[] = [];
    const seenLinks = new Set<string>();

    rawData.forEach(item => {
      if (!moduleMap.has(item.module_id)) {
        moduleMap.set(item.module_id, {
          module_id: item.module_id,
          module_name: item.module_generic_name,
          pillar: item.math_study_pillar,
          year: item.year,
          requires_1: item.requires_1,
          requires_2: item.requires_2,
          submodules: [],
        });

        // Add prerequisite links
        if (item.requires_1) {
          const linkKey = `${item.requires_1}->${item.module_id}`;
          if (!seenLinks.has(linkKey)) {
            links.push({ from: item.requires_1, to: item.module_id });
            seenLinks.add(linkKey);
          }
        }
        if (item.requires_2) {
          const linkKey = `${item.requires_2}->${item.module_id}`;
          if (!seenLinks.has(linkKey)) {
            links.push({ from: item.requires_2, to: item.module_id });
            seenLinks.add(linkKey);
          }
        }
      }

      const module = moduleMap.get(item.module_id)!;
      // Only add submodule if we haven't seen this order yet (dedupe)
      if (!module.submodules.some(s => s.order === item.submodule_order)) {
        module.submodules.push({ name: item.submodule, order: item.submodule_order });
      }
    });

    // Sort submodules by order
    moduleMap.forEach(module => {
      module.submodules.sort((a, b) => a.order - b.order);
    });

    // Group by year
    const byYear = new Map<number, ProcessedModule[]>();
    moduleMap.forEach(module => {
      if (!byYear.has(module.year)) byYear.set(module.year, []);
      byYear.get(module.year)!.push(module);
    });

    // Sort modules within each year by pillar
    byYear.forEach(modules => {
      modules.sort((a, b) => {
        const aIdx = PILLAR_ORDER.indexOf(a.pillar);
        const bIdx = PILLAR_ORDER.indexOf(b.pillar);
        if (aIdx !== bIdx) return aIdx - bIdx;
        return a.module_name.localeCompare(b.module_name);
      });
    });

    // Build list of all submodule keys
    const subKeys: string[] = [];
    moduleMap.forEach(module => {
      module.submodules.forEach(sub => {
        subKeys.push(`${module.module_id}_${sub.order}`);
      });
    });

    return { modulesByYear: byYear, allSubmoduleKeys: subKeys, prerequisiteLinks: links };
  }, [rawData]);

  // Layout constants
  const nodeRadius = 10;
  const submoduleGap = 28;
  const moduleGapX = 160;
  const yearGap = 40;
  const yearLabelWidth = 50;
  const paddingX = 30;
  const paddingY = 20;

  // Calculate node positions
  const { nodePositions, svgWidth, svgHeight } = useMemo(() => {
    const positions: Record<string, { x: number; y: number; moduleId: string; year: number }> = {};
    const modulePositions: Record<string, { x: number; y: number; height: number }> = {};

    const sortedYears = Array.from(modulesByYear.keys()).sort((a, b) => a - b);
    let currentY = paddingY;

    sortedYears.forEach(year => {
      const modules = modulesByYear.get(year)!;
      const maxSubmodules = Math.max(...modules.map((m: ProcessedModule) => m.submodules.length));
      const yearHeight = maxSubmodules * submoduleGap;

      modules.forEach((module: ProcessedModule, moduleIdx: number) => {
        const x = yearLabelWidth + paddingX + moduleIdx * moduleGapX;
        const moduleStartY = currentY;

        modulePositions[module.module_id] = {
          x,
          y: moduleStartY,
          height: module.submodules.length * submoduleGap,
        };

        module.submodules.forEach((sub: { name: string; order: number }, subIdx: number) => {
          const key = `${module.module_id}_${sub.order}`;
          positions[key] = {
            x,
            y: moduleStartY + subIdx * submoduleGap,
            moduleId: module.module_id,
            year: module.year,
          };
        });
      });

      currentY += yearHeight + yearGap;
    });

    const maxModules = Math.max(...Array.from(modulesByYear.values()).map((m: ProcessedModule[]) => m.length));
    const width = yearLabelWidth + paddingX * 2 + maxModules * moduleGapX;
    const height = currentY + paddingY;

    return { nodePositions: positions, modulePositions, svgWidth: width, svgHeight: height };
  }, [modulesByYear]);

  // Initialize mastery
  const initializeMastery = useCallback(() => {
    const initial: Record<string, MasteryLevel> = {};
    allSubmoduleKeys.forEach(key => {
      const pos = nodePositions[key];
      if (pos && pos.year <= 2) {
        // Pre-fill Years 0-2
        const rand = Math.random();
        if (rand < 0.35) initial[key] = 'gold';
        else if (rand < 0.7) initial[key] = 'silver';
        else initial[key] = 'bronze';
      } else {
        initial[key] = 'none';
      }
    });
    return initial;
  }, [allSubmoduleKeys, nodePositions]);

  useEffect(() => {
    if (allSubmoduleKeys.length > 0) {
      setMastery(initializeMastery());
      setLogs([{
        time: 0,
        type: 'system',
        moduleId: '',
        submodule: '',
        message: 'Student profile loaded. Years K-2 mastered. Simulating Years 3-12.',
      }]);
    }
  }, [allSubmoduleKeys, initializeMastery]);

  // Generate simulation events starting from Year 3 and continuing through all years
  const simulationEvents = useMemo(() => {
    const events: { time: number; type: string; key: string; moduleId: string; submodule: string }[] = [];
    let time = 1;

    // Get all years from 3 onwards, sorted
    const yearsToSimulate = Array.from(modulesByYear.keys())
      .filter(year => year >= 3)
      .sort((a, b) => a - b);

    yearsToSimulate.forEach(year => {
      const yearModules = modulesByYear.get(year) || [];

      yearModules.forEach((module: ProcessedModule) => {
        module.submodules.forEach((sub: { name: string; order: number }) => {
          const key = `${module.module_id}_${sub.order}`;
          // Add correct events (most), some incorrect, some decay on earlier years
          events.push({ time: time++, type: 'correct', key, moduleId: module.module_id, submodule: sub.name });
          if (Math.random() < 0.3) {
            events.push({ time: time++, type: 'incorrect', key, moduleId: module.module_id, submodule: sub.name });
          }
          events.push({ time: time++, type: 'correct', key, moduleId: module.module_id, submodule: sub.name });
        });

        // Add occasional decay on earlier mastered modules
        if (Math.random() < 0.3) {
          // Pick a random earlier year to decay
          const earlierYears = Array.from(modulesByYear.keys()).filter(y => y < year);
          if (earlierYears.length > 0) {
            const decayYear = earlierYears[Math.floor(Math.random() * earlierYears.length)];
            const decayYearModules = modulesByYear.get(decayYear) || [];
            if (decayYearModules.length > 0) {
              const decayModule = decayYearModules[Math.floor(Math.random() * decayYearModules.length)];
              if (decayModule.submodules.length > 0) {
                const decaySub = decayModule.submodules[Math.floor(Math.random() * decayModule.submodules.length)];
                events.push({
                  time: time++,
                  type: 'decay',
                  key: `${decayModule.module_id}_${decaySub.order}`,
                  moduleId: decayModule.module_id,
                  submodule: decaySub.name,
                });
              }
            }
          }
        }
      });
    });

    return events;
  }, [modulesByYear]);

  const maxSimTime = simulationEvents.length > 0 ? Math.max(...simulationEvents.map(e => e.time)) : 30;

  const getMasteryChange = useCallback((current: MasteryLevel, direction: 'up' | 'down'): MasteryLevel => {
    const order: MasteryLevel[] = ['none', 'bronze', 'silver', 'gold'];
    const idx = order.indexOf(current);
    if (direction === 'up') return order[Math.min(idx + 1, order.length - 1)];
    return order[Math.max(idx - 1, 0)];
  }, []);

  // Run simulation
  useEffect(() => {
    if (!isPlaying || simulationEvents.length === 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setSimTime(prev => {
        const newTime = prev + 1;
        const currentEvents = simulationEvents.filter(e => e.time === newTime);

        currentEvents.forEach(event => {
          setMastery(prevMastery => {
            const currentLevel = prevMastery[event.key] || 'none';
            let newLevel: MasteryLevel;
            let logType: LogEntry['type'];
            let message: string;

            if (event.type === 'correct') {
              newLevel = getMasteryChange(currentLevel, 'up');
              logType = 'correct';
              message = 'Correct! Mastery increased.';
            } else if (event.type === 'incorrect') {
              newLevel = getMasteryChange(currentLevel, 'down');
              logType = 'incorrect';
              message = 'Incorrect. Review needed.';
            } else {
              newLevel = getMasteryChange(currentLevel, 'down');
              logType = 'decay';
              message = 'Time decay - needs review.';
            }

            setActiveSubmodule(event.key);
            setTimeout(() => setActiveSubmodule(null), 500);

            setLogs(prevLogs => [...prevLogs, {
              time: newTime,
              type: logType,
              moduleId: event.moduleId,
              submodule: event.submodule,
              message,
              masteryChange: { from: currentLevel, to: newLevel },
            }]);

            return { ...prevMastery, [event.key]: newLevel };
          });
        });

        if (newTime >= maxSimTime) setIsPlaying(false);
        return newTime;
      });
    }, 800 / speed);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, speed, simulationEvents, maxSimTime, getMasteryChange]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const reset = () => {
    setIsPlaying(false);
    setSimTime(0);
    setMastery(initializeMastery());
    setLogs([{
      time: 0,
      type: 'system',
      moduleId: '',
      submodule: '',
      message: 'Student profile loaded. Years K-2 mastered. Simulating Years 3-12.',
    }]);
  };

  // These must be before any conditional returns to satisfy Rules of Hooks
  const sortedYears = useMemo(() =>
    Array.from(modulesByYear.keys()).sort((a, b) => a - b),
    [modulesByYear]
  );

  const yearLabelPositions = useMemo(() => {
    const positions: Record<number, number> = {};
    let currentY = paddingY;

    sortedYears.forEach(year => {
      const modules = modulesByYear.get(year);
      if (!modules) return;
      const maxSubmodules = Math.max(...modules.map((m: ProcessedModule) => m.submodules.length));
      positions[year] = currentY;
      currentY += maxSubmodules * submoduleGap + yearGap;
    });

    return positions;
  }, [sortedYears, modulesByYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[#94a3b8]">Loading curriculum data...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-3 pb-2 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Mastery System Demo</h2>
          <p className="text-xs text-[#94a3b8]">Submodules stacked vertically under each module</p>
        </div>
        <div className="flex gap-3">
          {PILLAR_ORDER.slice(0, 3).map(pillar => (
            <div key={pillar} className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: PILLAR_COLORS[pillar] }} />
              <span className="text-[10px] text-[#94a3b8]">{PILLAR_NAMES[pillar]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-3 px-6 pb-2 min-h-0">
        {/* Left: Tree */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#64748b]">Curriculum Tree (modules with submodules)</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} className="p-0.5 rounded hover:bg-[#334155] text-[#94a3b8]">
                <MagnifyingGlassMinusIcon className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] text-[#64748b] w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(1.2, z + 0.1))} className="p-0.5 rounded hover:bg-[#334155] text-[#94a3b8]">
                <MagnifyingGlassPlusIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div
            className="flex-1 overflow-auto rounded-lg relative"
            style={{ background: 'rgba(10,15,30,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <svg
              width={svgWidth * zoom}
              height={svgHeight * zoom}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              style={{ minWidth: svgWidth * zoom }}
            >
              <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <marker id="arrow" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
                  <polygon points="0 0, 5 2.5, 0 5" fill="#475569" />
                </marker>
              </defs>

              {/* Year labels */}
              {sortedYears.map(year => (
                <text
                  key={`year-${year}`}
                  x={paddingX - 10}
                  y={yearLabelPositions[year] + 10}
                  fill={year <= 2 ? '#64748b' : '#94a3b8'}
                  fontSize="10"
                  fontWeight="600"
                >
                  {year === 0 ? 'K' : `Y${year}`}
                </text>
              ))}

              {/* Prerequisite links between modules */}
              {prerequisiteLinks.map((link, idx) => {
                // Find first submodule position of each module
                const fromModule = Array.from(modulesByYear.values()).flat().find(m => m.module_id === link.from);
                const toModule = Array.from(modulesByYear.values()).flat().find(m => m.module_id === link.to);

                if (!fromModule || !toModule) return null;

                const fromKey = `${fromModule.module_id}_${fromModule.submodules[fromModule.submodules.length - 1]?.order || 1}`;
                const toKey = `${toModule.module_id}_${toModule.submodules[0]?.order || 1}`;

                const fromPos = nodePositions[fromKey];
                const toPos = nodePositions[toKey];

                if (!fromPos || !toPos) return null;

                const dx = toPos.x - fromPos.x;

                if (Math.abs(dx) < 10) {
                  // Same column - straight line
                  return (
                    <line
                      key={idx}
                      x1={fromPos.x}
                      y1={fromPos.y + nodeRadius + 2}
                      x2={toPos.x}
                      y2={toPos.y - nodeRadius - 4}
                      stroke="#475569"
                      strokeWidth={1}
                      opacity={0.5}
                      markerEnd="url(#arrow)"
                    />
                  );
                }

                // Curved path
                const midY = (fromPos.y + toPos.y) / 2;
                return (
                  <path
                    key={idx}
                    d={`M ${fromPos.x} ${fromPos.y + nodeRadius + 2}
                        Q ${fromPos.x} ${midY}, ${(fromPos.x + toPos.x) / 2} ${midY}
                        Q ${toPos.x} ${midY}, ${toPos.x} ${toPos.y - nodeRadius - 4}`}
                    fill="none"
                    stroke="#475569"
                    strokeWidth={1}
                    opacity={0.4}
                    markerEnd="url(#arrow)"
                  />
                );
              })}

              {/* Modules and submodules */}
              {sortedYears.map(year => {
                const modules = modulesByYear.get(year)!;
                return modules.map((module: ProcessedModule) => (
                  <g key={module.module_id}>
                    {/* Module label */}
                    {module.submodules[0] && (() => {
                      const firstKey = `${module.module_id}_${module.submodules[0].order}`;
                      const pos = nodePositions[firstKey];
                      if (!pos) return null;
                      return (
                        <text
                          x={pos.x + nodeRadius + 8}
                          y={pos.y - 6}
                          fill={year <= 2 ? '#64748b' : '#e2e8f0'}
                          fontSize="8"
                          fontWeight="500"
                        >
                          {module.module_name.length > 16 ? module.module_name.slice(0, 14) + '...' : module.module_name}
                        </text>
                      );
                    })()}

                    {/* Submodules */}
                    {module.submodules.map((sub: { name: string; order: number }) => {
                      const key = `${module.module_id}_${sub.order}`;
                      const pos = nodePositions[key];
                      if (!pos) return null;

                      const level = mastery[key] || 'none';
                      const progress = MASTERY_PROGRESS[level];
                      const isActive = activeSubmodule === key;
                      const circumference = 2 * Math.PI * (nodeRadius + 3);
                      const masteryPercent = Math.round(progress * 100);

                      return (
                        <g
                          key={key}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={() => setHoveredNode({ key, x: pos.x, y: pos.y, moduleName: module.module_name, subName: sub.name })}
                          onMouseLeave={() => setHoveredNode(null)}
                        >
                          {/* Background ring */}
                          <circle cx={pos.x} cy={pos.y} r={nodeRadius + 3} fill="none" stroke="#1e293b" strokeWidth={3} />
                          {/* Progress ring */}
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={nodeRadius + 3}
                            fill="none"
                            stroke={MASTERY_COLORS[level]}
                            strokeWidth={3}
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference * (1 - progress)}
                            strokeLinecap="round"
                            transform={`rotate(-90 ${pos.x} ${pos.y})`}
                            style={{ transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease' }}
                          />
                          {/* Inner node */}
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={isActive ? nodeRadius + 1 : nodeRadius}
                            fill={PILLAR_COLORS[module.pillar] || '#94a3b8'}
                            filter={isActive ? 'url(#glow)' : undefined}
                            style={{ transition: 'r 0.15s ease' }}
                          />
                          {/* Mastery indicator inside */}
                          {level !== 'none' && (
                            <text
                              x={pos.x}
                              y={pos.y + 1}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="bold"
                              style={{ pointerEvents: 'none', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                            >
                              {masteryPercent}
                            </text>
                          )}
                          {/* Vertical connector to next submodule */}
                          {sub.order < module.submodules.length && (
                            <line
                              x1={pos.x}
                              y1={pos.y + nodeRadius + 4}
                              x2={pos.x}
                              y2={pos.y + submoduleGap - nodeRadius - 4}
                              stroke={PILLAR_COLORS[module.pillar]}
                              strokeWidth={1}
                              opacity={0.3}
                            />
                          )}
                        </g>
                      );
                    })}
                  </g>
                ));
              })}
            </svg>

            {/* Tooltip */}
            {hoveredNode && (
              <div
                className="absolute pointer-events-none z-10 px-2 py-1.5 rounded-lg shadow-lg"
                style={{
                  left: hoveredNode.x * zoom + 20,
                  top: hoveredNode.y * zoom - 10,
                  background: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  maxWidth: 200,
                }}
              >
                <div className="text-xs font-semibold text-white">{hoveredNode.moduleName}</div>
                <div className="text-[10px] text-[#94a3b8]">{hoveredNode.subName}</div>
                <div className="text-[10px] mt-1 flex items-center gap-1">
                  <span className="text-[#64748b]">Mastery:</span>
                  <span
                    className="font-semibold capitalize"
                    style={{ color: MASTERY_COLORS[mastery[hoveredNode.key] || 'none'] }}
                  >
                    {mastery[hoveredNode.key] === 'none' ? 'Not started' : `${mastery[hoveredNode.key]} (${Math.round(MASTERY_PROGRESS[mastery[hoveredNode.key] || 'none'] * 100)}%)`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Log */}
        <div className="w-64 flex flex-col">
          <span className="text-xs text-[#64748b] mb-1">Activity Log</span>
          <div
            ref={logRef}
            className="flex-1 overflow-y-auto rounded-lg p-2 space-y-1.5"
            style={{ background: 'rgba(10,15,30,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <AnimatePresence>
              {logs.map((log, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-[10px] p-1.5 rounded"
                  style={{
                    background: log.type === 'correct' ? 'rgba(16,185,129,0.15)' :
                               log.type === 'incorrect' ? 'rgba(239,68,68,0.15)' :
                               log.type === 'decay' ? 'rgba(245,158,11,0.15)' : 'rgba(30,41,59,0.5)',
                    borderLeft: `2px solid ${
                      log.type === 'correct' ? '#10b981' :
                      log.type === 'incorrect' ? '#ef4444' :
                      log.type === 'decay' ? '#f59e0b' : '#64748b'
                    }`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748b]">T+{log.time}s</span>
                    {log.masteryChange && (
                      <div className="flex items-center gap-0.5">
                        <span className="px-1 rounded text-[8px]" style={{ background: `${MASTERY_COLORS[log.masteryChange.from]}40`, color: MASTERY_COLORS[log.masteryChange.from] }}>
                          {log.masteryChange.from === 'none' ? 'new' : log.masteryChange.from}
                        </span>
                        <span className="text-[#64748b]">→</span>
                        <span className="px-1 rounded text-[8px]" style={{ background: `${MASTERY_COLORS[log.masteryChange.to]}40`, color: MASTERY_COLORS[log.masteryChange.to] }}>
                          {log.masteryChange.to === 'none' ? 'new' : log.masteryChange.to}
                        </span>
                      </div>
                    )}
                  </div>
                  {log.submodule && <div className="text-white font-medium truncate">{log.submodule}</div>}
                  <div className="text-[#94a3b8]">{log.message}</div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Legend */}
          <div className="mt-1.5 p-1.5 rounded flex justify-between" style={{ background: 'rgba(30,41,59,0.5)' }}>
            {(['none', 'bronze', 'silver', 'gold'] as MasteryLevel[]).map(level => (
              <div key={level} className="flex items-center gap-0.5">
                <div className="w-2 h-2 rounded-full" style={{ background: MASTERY_COLORS[level] }} />
                <span className="text-[8px] text-[#94a3b8] capitalize">{level === 'none' ? 'New' : level}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-6 py-2 flex items-center justify-center gap-4" style={{ background: 'rgba(15,23,42,0.9)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-medium text-sm"
          style={{
            background: isPlaying ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
            border: `2px solid ${isPlaying ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)'}`,
            color: isPlaying ? '#ef4444' : '#10b981',
          }}
        >
          {isPlaying ? <><PauseIcon className="w-3.5 h-3.5" /> Pause</> : <><PlayIcon className="w-3.5 h-3.5" /> {simTime > 0 ? 'Resume' : 'Play'}</>}
        </button>

        <div className="flex items-center gap-1.5">
          <span className="text-[#64748b] text-xs">Speed:</span>
          {[1, 2, 3].map(s => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`px-2 py-0.5 rounded text-xs font-medium ${speed === s ? 'bg-[#0ea5e9] text-white' : 'bg-[#1e293b] text-[#94a3b8]'}`}
            >
              {s}x
            </button>
          ))}
        </div>

        <button onClick={reset} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-[#94a3b8] hover:text-white hover:bg-[#334155]">
          <ArrowPathIcon className="w-3.5 h-3.5" /> Reset
        </button>

        <div className="text-[#64748b] text-xs">
          <span className="text-white font-mono">{simTime}</span> / {maxSimTime}s
        </div>
      </div>
    </div>
  );
};

export default InvSlide04bMasteryDemo;
